import React from 'react';

import { EyebrowTitle } from '@common/components/Typography';
import { useTranslations } from '@lang';
import BASKET_UPDATE_TYPES from '@common/constants/BasketUpdateTypes';
import { ContentPage, Item } from 'shared-types/Common';
import { Event, EventProps } from './Event';
import { Product, ProductProps } from './Product';
import { Room, RoomProps } from './Room';
import { SpaItem, SpaItemProps } from './SpaItem';
import { Table, TableProps } from './Table';
import { Voucher, VoucherProps } from './Voucher';

import { useLabels } from '@features/Menu/useLabels';
import { useGetSpaSettings } from '@features/spa';
import { mapItemsWithVouchers } from '@common/helpers/mapItemsWithVouchers';
import { BasketVoucher } from 'shared-types/Basket';
import { Package } from './Package/Package';
import { groupByItemType } from './helpers/groupByItemType';
import { separateAndGroupPackageAndNonPackageItems } from './helpers/separateAndGroupPackageAndNonPackageItems';
import { ItemCategory } from './styled';

type ItemListingRowsProps = {
  onChange?: (
    type: BASKET_UPDATE_TYPES.QUANTITY | BASKET_UPDATE_TYPES.REMOVE,
    itemId: number | string,
    quantity?: number
  ) => void;
  items: Array<Item>;
  isEditable: boolean;
  renderDelivery?: (deliveryDetails?: any) => React.ReactElement | null;
  spaClassesPage?: ContentPage;
  tablesPage: ContentPage;
  tablesDisableChildBookings: boolean;
  propertyId?: number;
  basketVouchers?: Array<BasketVoucher>;
};

export type GroupedBasketItemTypes = {
  tables?: Array<TableProps>;
  products?: Array<ProductProps>;
  rooms?: Array<RoomProps>;
  'spa-facilities'?: Array<SpaItemProps>;
  'spa-treatments'?: Array<SpaItemProps>;
  'spa-packages'?: Array<SpaItemProps>;
  'spa-classes'?: Array<SpaItemProps>;
  spa?: Array<Item>;
  packages?: Array<{ [itemType: string]: Item[] }>;
  vouchers?: Array<VoucherProps>;
  events?: Array<EventProps>;
};

export const ItemListingRows: React.FC<ItemListingRowsProps> = ({
  onChange = () => {},
  items,
  isEditable,
  renderDelivery,
  tablesPage,
  spaClassesPage,
  tablesDisableChildBookings,
  propertyId,
  basketVouchers: basketVouchers,
}) => {
  const { t } = useTranslations();

  const itemsWithVouchers = mapItemsWithVouchers(items, basketVouchers);
  const groupedItems = separateAndGroupPackageAndNonPackageItems(
    itemsWithVouchers,
    groupByItemType
  );
  const {
    tables,
    products,
    rooms,
    'spa-treatments': spaTreatments,
    'spa-facilities': spaFacilities,
    'spa-packages': spaPackages,
    'spa-classes': spaClasses,
    packages,
    vouchers,
    events,
  } = groupedItems;
  const { data: spaSettings } = useGetSpaSettings(propertyId || 0);
  const { hideArrivalTimes } = spaSettings || { hideArrivalTimes: false };
  const labels = useLabels();

  return (
    <div>
      {packages &&
        packages.map((item, index) => {
          return (
            <Package
              key={`basket-package-${index}`}
              items={item}
              onChange={onChange}
              isEditable={isEditable}
              tablesPage={tablesPage}
              tablesDisableChildBookings={tablesDisableChildBookings}
            />
          );
        })}
      {rooms && (
        <ItemCategory>
          <EyebrowTitle>{labels?.rooms || t('basket.rooms')}</EyebrowTitle>
          {rooms.map(({ item }) => (
            <Room key={item.id} item={item} onChange={onChange} isEditable={isEditable} />
          ))}
        </ItemCategory>
      )}
      {(spaFacilities || spaTreatments || spaPackages || spaClasses) && (
        <ItemCategory>
          <EyebrowTitle>{labels?.spa || t('basket.spa')}</EyebrowTitle>
          {spaPackages?.map(({ item }) => (
            <SpaItem
              key={item.id}
              item={item}
              onChange={onChange}
              isEditable={isEditable}
              hideArrivalTimes={hideArrivalTimes}
            />
          ))}
          {spaTreatments?.map(({ item }) => (
            <SpaItem key={item.id} item={item} onChange={onChange} isEditable={isEditable} />
          ))}
          {spaClasses?.map(({ item }) => (
            <SpaItem
              key={item.id}
              item={{
                ...item,
                bookable: {
                  ...item.bookable,
                  imageUrl: item.imageUrl || spaClassesPage?.heroBackgroundImage?.url,
                },
              }}
              onChange={onChange}
              isEditable={isEditable}
            />
          ))}
          {spaFacilities?.map(({ item }) => (
            <SpaItem key={item.id} item={item} onChange={onChange} isEditable={isEditable} />
          ))}
        </ItemCategory>
      )}
      {products && (
        <ItemCategory>
          <EyebrowTitle>{labels?.retail || t('basket.products')}</EyebrowTitle>
          {products.map(({ item }) => (
            <Product
              key={item.product.id}
              item={item}
              onChange={onChange}
              isEditable={isEditable}
            />
          ))}
          {renderDelivery && renderDelivery(products)}
        </ItemCategory>
      )}
      {tables && (
        <ItemCategory>
          <EyebrowTitle>{labels?.tables || t('basket.diningReservation')}</EyebrowTitle>
          {tables.map(({ item }) => (
            <Table
              key={item.id}
              item={item}
              onChange={onChange}
              isEditable={isEditable}
              thumbnail={tablesPage?.heroBackgroundImage?.url}
              tablesDisableChildBookings={tablesDisableChildBookings}
              basketVouchers={basketVouchers}
            />
          ))}
        </ItemCategory>
      )}
      {vouchers && (
        <ItemCategory>
          <EyebrowTitle>{labels?.vouchers || t('basket.giftVouchers')}</EyebrowTitle>
          {vouchers.map(({ item }) => (
            <Voucher
              key={item.id}
              item={item}
              onChange={onChange}
              isQuantityPickerHidden
              isEditable={isEditable}
            />
          ))}
          {renderDelivery && renderDelivery(vouchers)}
        </ItemCategory>
      )}
      {events && (
        <ItemCategory>
          <EyebrowTitle>{labels?.events || t('basket.events')}</EyebrowTitle>
          {events.map(({ item }) => (
            <Event
              key={item.id}
              item={item}
              onChange={onChange}
              isQuantityPickerHidden
              isEditable={isEditable}
            />
          ))}
          {renderDelivery && renderDelivery(events)}
        </ItemCategory>
      )}
    </div>
  );
};
